import {useAccessToken, useIsBankAdminOrOperationsUser, useUserClaimsData} from "../../../services/auth"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {Org} from "../../../resources/org"
import React from "react"
import {MainSection} from "../../../containers/MainSection/MainSection"
import {Customer} from "../../../resources/customer"
import classNames from "classnames"
import {useQueryState} from "use-location-state"
import OrgOverview from "../OrgOverview"
import {isLocalEnv, isSandboxEnv} from "../../../utilities/environment"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import OrgTitleBar from "../OrgPageComponents/OrgTitleBar"
import OrgPageWithoutOnboardingRules from "../OrgPageComponents/OrgPageWithoutOnboardingRules"
import {findAgreements, OrgBankAgreement} from "../../../resources/orgBankAgreement"
import OrgRiskSettings from "../RiskSettings/OrgRiskSettings"
import Loading from "../../../components/Loading/Loading"
import {Err} from "../../../components/Err/Err"

type OrgTabs = "Overview" | "Onboarding Rules"

const sandboxBankIds = ["20", "26"]

interface PageProps {
	customers: Customer[]
	org: Org
	currentDefaultBankName?: string
	bankNames?: string[]
	refresh: () => void
	refreshToken: number
	orgBankAgreement: OrgBankAgreement
}

const Page = ({
	customers,
	org,
	currentDefaultBankName,
	bankNames,
	refresh,
	refreshToken,
	orgBankAgreement,
}: PageProps) => {
	const claims = useUserClaimsData()
	const isBankAdminOrOperationsUser = useIsBankAdminOrOperationsUser()
	const showOnboardingRulesTabs =
		isBankAdminOrOperationsUser && (isLocalEnv() || (isSandboxEnv() && sandboxBankIds.includes(claims.bankId)))
	const [currentTab, setCurrentTab] = useQueryState<OrgTabs>("tab", "Overview")
	return showOnboardingRulesTabs ? (
		<>
			<OrgTitleBar org={org} customers={customers} refresh={refresh} />
			<nav className="org-settings tabs-container">
				<div className="tabs">
					<ul>
						<li
							className={classNames(currentTab == "Overview" && "is-active")}
							onClick={() => setCurrentTab("Overview")}
						>
							<a>Overview</a>
						</li>
						<li
							className={classNames(currentTab === "Onboarding Rules" && "is-active")}
							onClick={() => setCurrentTab("Onboarding Rules")}
						>
							<a>Onboarding Rules</a>
						</li>
					</ul>
				</div>
			</nav>
			<MainSection>
				{currentTab === "Overview" && (
					<OrgOverview
						org={org}
						currentDefaultBankName={currentDefaultBankName}
						customers={customers}
						bankNames={bankNames}
						refresh={refresh}
					/>
				)}
				{currentTab === "Onboarding Rules" && (
					<OrgRiskSettings
						agreementId={orgBankAgreement.id}
						org={org}
						bankId={orgBankAgreement.relationships.bank.data.id}
						bankRiskSettings={orgBankAgreement.attributes.bankRiskSettings}
						refresh={refresh}
						refreshToken={refreshToken}
					/>
				)}
			</MainSection>
		</>
	) : (
		<OrgPageWithoutOnboardingRules
			org={org}
			customers={customers}
			refresh={refresh}
			currentDefaultBankName={currentDefaultBankName}
			bankNames={bankNames}
		/>
	)
}

interface OrgPageForUnitUserProps {
	org: Org
	currentDefaultBankName?: string
	customers: Customer[]
	bankNames: string[]
	refresh: () => void
	refreshToken: number
}

export default function OrgPageForBankUser({
	org,
	currentDefaultBankName,
	customers,
	bankNames,
	refresh,
	refreshToken,
}: OrgPageForUnitUserProps) {
	const accessToken = useAccessToken()
	const orgBankAgreements = useAsyncResult(
		() => findAgreements({accessToken, offset: 0, limit: 1, orgs: [org.id], enabledOnly: true}),
		[refreshToken]
	) // Pulling the only OBA for the org, since it's a bank user and a bank can have only one OBA
	return (
		<AsyncResultComponent asyncResult={orgBankAgreements} pendingComponent={<Loading />}>
			{({value: orgBankAgreements}) => {
				if (orgBankAgreements.data.length === 0) {
					return <Err err="No Org Bank Agreement Found." />
				}
				return (
					<Page
						org={org}
						currentDefaultBankName={currentDefaultBankName}
						customers={customers}
						bankNames={bankNames}
						refresh={refresh}
						refreshToken={refreshToken}
						orgBankAgreement={orgBankAgreements.data[0]}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
